import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Props {
  open: boolean;
  title: string;
  initialValue?: string;
  onClose: () => void;
  onSave: (value: string) => void;
}

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 700,
    minHeight: 500,
  },
  helpText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  editorContainer: {
    height: 400,
    '& .ql-container': {
      height: 'calc(100% - 42px)',
      fontSize: 14,
    },
    '& .ql-toolbar': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      backgroundColor: 'white',
    },
    '& .ql-editor': {
      minHeight: 300,
      '& img': {
        display: 'block',
        maxWidth: '300px',
        marginLeft: 'auto',
        marginRight: 'auto',
      },
      '& p': {
        marginBottom: '0.5em',
        marginTop: '0.5em',
      },
      '& h1': {
        marginTop: '0.5em',
      },
      '& h2': {
        marginTop: '0.5em',
      },
      '& h3': {
        marginTop: '0.5em',
      },
    },
  },
}));

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
  'image',
];

const MarkdownEditorModal: React.FC<Props> = ({
  open,
  title,
  initialValue = '',
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(initialValue);
  const quillRef = useRef<ReactQuill>(null);

  const imageHandler = useCallback(() => {
    const url = prompt('Enter the image URL');
    if (url && quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'image', url, 'user');
    }
  }, []);

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['image'],
      ],
      handlers: {
        image: imageHandler,
      },
    },
    clipboard: {
      matchVisual: false,
    },
  };

  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  const handleSave = () => {
    console.log('value', value);
    onSave(value);
    onClose();
  };

  const handleCancel = () => {
    setValue(initialValue);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.editorContainer}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={setValue}
            modules={modules}
            formats={formats}
            placeholder="Enter your content here..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="default">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkdownEditorModal;
